import { useContext, useEffect, useState } from 'react';
import './SavedReports.scss';
import {
  Table, TableHead, TableRow, TableBody, TableContainer, Paper, Typography, Chip, Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Card,
} from '@mui/material';
import { format } from 'date-fns';
import {
  deleteData,
  downloadURI,
  getAuthTokensFromBrowser, getData, postData, StyledTableCell, StyledTableRow,
} from '../../../utils/utils';
import { DataContext } from '../../../context/DataContext';
import backgroundImage from '../../../images/FaintBackground.jpg';

function SavedReports() {
  const [reports, setReports] = useState<any[]>([]);
  const { loading, setLoading, setMessagebar } = useContext(DataContext);
  const [showDeleteReportDialog, setShowDeleteReportDialog] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<any>(null);

  useEffect(() => {
    setLoading(true);
    const fetchReportsWithTags = async () => {
      try {
        const defaultOrgId = getAuthTokensFromBrowser().active_org_session;
        const reportResponse = await getData(`/reports/organisation/${defaultOrgId}?multiOrgReport=true`);
        const reportsList = reportResponse?.data ?? [];

        // Fetch tags for each report's organisation
        const orgIdsArray = [...new Set(reportsList.map((report: any) => report.organisation))];

        const orgResponses = await Promise.all(
          orgIdsArray.map((id) => getData(`/organisations/${id}`)),
        );

        const getAllTags = orgResponses.reduce((arr: any, res: any) => {
          arr.push({ tags: res?.data?.tags, organisation: res?.data?.id });
          return arr;
        }, []);

        const reportsWithTags = reportsList.reduce((arr: any[], report: any) => {
          // eslint-disable-next-line max-len
          const tagMatch = getAllTags.find((orgTags: any) => orgTags.organisation === report.organisation);
          const updateReports = {
            ...report,
            tags: tagMatch?.tags || [],
          };
          arr.push(updateReports);
          return arr;
        }, []);

        setReports(reportsWithTags);
        setLoading(false);
      } catch (err) {
        setLoading(false);
      }
    };

    fetchReportsWithTags();
  }, [setLoading]);

  const handleFinalDeleteReport = async () => {
    try {
      setLoading(true);
      await deleteData(`/reports/${reportToDelete.id}`);
      setReports((prev) => prev.filter((r) => r.id !== reportToDelete.id));
      setMessagebar({
        show: true,
        text: 'Report deleted successfully.',
        severity: 'success',
      });
    } catch (err) {
      setMessagebar({
        show: true,
        text: 'Failed to delete report.',
        severity: 'error',
      });
    } finally {
      setShowDeleteReportDialog(false);
      setReportToDelete(null);
      setLoading(false);
    }
  };

  const confirmDeleteReport = (report: any) => {
    setReportToDelete(report);
    setShowDeleteReportDialog(true);
  };

  const handleDownloadReport = async (report: any) => {
    try {
      const payload = {
        report_id: report.id,
        organisation: report.organisation,
        aggregate: true,
        report_types: report.report_types,
        start_date: report.start_date,
        end_date: report.end_date,
        multi_org_reports: true,
      };

      setLoading(true);
      const res = await postData('/reports/download-instant', payload);
      if (res.status === 200) {
        const { data } = res;
        const fileName = data.split('filename=')[1].split(';')[0];
        downloadURI(data, fileName);
      }
    } catch (error) {
      setMessagebar({
        show: true,
        text: 'Download failed. Try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!loading && (
        <Card
          sx={{
            margin: '20px',
            borderRadius: '20px',
            padding: '40px',
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            top: '0px',
            right: '0px',
            bottom: '0px',
            left: '0px',
            opacity: 1,
            filter: 'brightness(1.05)',
            minHeight: 'calc(100% - 50px)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div className="administration">
            <Typography variant="h5" sx={{ my: 2 }}>
              Report Summary
            </Typography>

            {reports.length === 0 ? (
              <Typography sx={{
                textAlign: 'center', py: 20, color: '#999', fontSize: '2rem',
              }}
              >
                No saved reports found.
              </Typography>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Name</StyledTableCell>
                      <StyledTableCell>Description</StyledTableCell>
                      <StyledTableCell>Created At</StyledTableCell>
                      <StyledTableCell>Report Start Data - End Date</StyledTableCell>
                      <StyledTableCell>Tags</StyledTableCell>
                      <StyledTableCell>Action</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reports.map((report) => (
                      <StyledTableRow key={report.id}>
                        <StyledTableCell>{report.name}</StyledTableCell>
                        <StyledTableCell>{report.description}</StyledTableCell>
                        <StyledTableCell>
                          {new Date(report.created_at).toLocaleDateString()}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {`${format(new Date(report.start_date), 'MMMM d, yyyy')} - ${format(new Date(report.end_date), 'MMMM d, yyyy')}`}
                        </StyledTableCell>
                        <StyledTableCell>
                          <Box sx={{
                            display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center',
                          }}
                          >
                            {report.tags.map((tag: string) => (
                              <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                sx={{ backgroundColor: '#61b9c3', color: 'white' }}
                              />
                            ))}
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          <i
                            className="fas fa-download"
                            style={{ cursor: 'pointer', marginRight: '10px' }}
                            onClick={() => handleDownloadReport(report)}
                          />
                          <i
                            className="fas fa-trash"
                            style={{ cursor: 'pointer' }}
                            onClick={() => confirmDeleteReport(report)}
                          />
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </div>
        </Card>
      )}

      <Dialog
        open={showDeleteReportDialog}
        onClose={() => setShowDeleteReportDialog(false)}
        PaperProps={{ style: { borderRadius: '10px' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f8f8f8',
          }}
        >
          <span />
          <i
            className="fas fa-times"
            style={{ cursor: 'pointer' }}
            onClick={() => setShowDeleteReportDialog(false)}
          />
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          <Typography variant="h6" gutterBottom>
            Are you sure you want to delete this report?
          </Typography>
          <Typography variant="body2" gutterBottom sx={{ justifyContent: 'center' }}>
            {reportToDelete?.name}
          </Typography>
          <Box mt={3} display="flex" justifyContent="center" gap={2}>
            <Button variant="outlined" onClick={() => setShowDeleteReportDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              style={{ backgroundColor: '#C36670', color: '#fff' }}
              onClick={() => handleFinalDeleteReport()}
            >
              Delete
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SavedReports;
